// Client-safe certification slug helper.
//
// This is the single source of truth for turning a certification name into the
// URL slug consumed by /certifications/[slug]. It lives in its own module (with
// no server-only imports like Prisma or the catalog JSON) so it can be imported
// from client components — e.g. the homepage certification search — without
// pulling server code into the browser bundle.
//
// Slugs are URL-safe `[a-z0-9-]+`: "+" becomes "-plus", punctuation becomes a
// hyphen, and runs of separators collapse. A few names keep a short override
// (CMA vs AAMA CMA; COP-GOV). Changing the transform requires a 308 from every
// legacy path — see CERT_SLUG_REDIRECTS and redirectCertSlug().

/**
 * Names whose URL is not a naive slugify of the display name. Used to keep
 * already-indexed paths stable and to split colliding acronyms (CMA).
 */
const CERT_SLUG_BY_NAME: Record<string, string> = {
  cma: "cma",
  "certified management accountant": "cma",
  "certified management accountant (cma)": "cma",
  "cma (aama)": "aama-cma",
  "certified medical assistant (aama)": "aama-cma",
  "certified medical assistant (cma)": "aama-cma",
  "certified medical assistant": "aama-cma",
  "usmle step 2": "usmle-step-2",
  "usmle step 2 ck": "usmle-step-2",
  "usmle step 2 clinical knowledge": "usmle-step-2",
  "usmle step 2 clinical knowledge (ck)": "usmle-step-2",
  "certified outsourcing professional - outsourcing governance (cop-gov)": "cop-gov",
  "certified outsourcing specialist- foundation principles (cop-fp)": "cop-fp",
  "microsoft certified: power platform developer associate":
    "power-platform-developer-associate-pl-400",
  "microsoft power platform developer associate":
    "power-platform-developer-associate-pl-400",
  cisa: "certified-information-systems-auditor-cisa",
  "certified information systems auditor": "certified-information-systems-auditor-cisa",
  "certified information systems auditor (cisa)":
    "certified-information-systems-auditor-cisa",
  cahims: "cahims",
  "certified associate in healthcare information and management systems": "cahims",
  "certified associate in healthcare information and management systems (cahims)": "cahims",
  cphims: "cphims",
  "certified professional in healthcare information and management systems": "cphims",
  "certified professional in healthcare information and management systems (cphims)": "cphims",
  rhia: "rhia",
  "registered health information administrator": "rhia",
  "registered health information administrator (rhia)": "rhia",
  rhit: "rhit",
  "registered health information technician": "rhit",
  "registered health information technician (rhit)": "rhit",
  cpc: "cpc",
  "certified professional coder": "cpc",
  "certified professional coder (cpc)": "cpc",
};

/** Overrides that existed before the URL-safe slugifier (used to rebuild old paths). */
const LEGACY_CERT_SLUG_BY_NAME: Record<string, string> = {
  cma: "cma",
  "certified management accountant": "cma",
  "certified management accountant (cma)": "cma",
  "cma (aama)": "aama-cma",
  "certified medical assistant (aama)": "aama-cma",
  "certified medical assistant (cma)": "aama-cma",
  "certified medical assistant": "aama-cma",
  "usmle step 2": "usmle-step-2",
  "usmle step 2 ck": "usmle-step-2",
  "usmle step 2 clinical knowledge": "usmle-step-2",
  "usmle step 2 clinical knowledge (ck)": "usmle-step-2",
};

/**
 * Legacy or guessed slugs that must 308 to the canonical path.
 * `/usmle-step-2` stays put — CK is a title change, not a URL change.
 */
export const CERT_SLUG_REDIRECTS: Record<string, string> = {
  "cma-(aama)": "aama-cma",
  "certified-medical-assistant-(aama)": "aama-cma",
  "certified-medical-assistant-(cma)": "aama-cma",
  "certified-medical-assistant": "aama-cma",
  "certified-management-accountant": "cma",
  "certified-management-accountant-(cma)": "cma",
  "usmle-step-2-ck": "usmle-step-2",
  "usmle-step-2-clinical-knowledge": "usmle-step-2",
};

/** Extra DB/search phrases so community matching survives a display-name change. */
const EXTRA_COMMUNITY_TERMS: Record<string, string[]> = {
  "aama-cma": ["CMA (AAMA)", "Certified Medical Assistant", "AAMA"],
  cma: ["Certified Management Accountant", "CMA"],
  "usmle-step-2": ["USMLE Step 2 CK", "USMLE Step 2", "Step 2 CK"],
  "comptia-security-plus": ["CompTIA Security+", "Security+", "Sec+"],
  "power-platform-developer-associate-pl-400": [
    "PL-400",
    "AB-400",
    "Microsoft Certified: Power Platform Developer Associate",
    "Microsoft Power Platform Developer Associate",
  ],
  "certified-information-systems-auditor-cisa": [
    "CISA",
    "Certified Information Systems Auditor",
    "Certified Information Systems Auditor (CISA)",
  ],
  cahims: [
    "CAHIMS",
    "Certified Associate in Healthcare Information and Management Systems",
    "Certified Associate in Healthcare Information and Management Systems (CAHIMS)",
  ],
  cphims: [
    "CPHIMS",
    "Certified Professional in Healthcare Information and Management Systems",
    "Certified Professional in Healthcare Information and Management Systems (CPHIMS)",
  ],
  rhia: [
    "RHIA",
    "Registered Health Information Administrator",
    "Registered Health Information Administrator (RHIA)",
  ],
  rhit: [
    "RHIT",
    "Registered Health Information Technician",
    "Registered Health Information Technician (RHIT)",
  ],
  cpc: [
    "CPC",
    "Certified Professional Coder",
    "Certified Professional Coder (CPC)",
    "AAPC CPC",
  ],
  "cop-gov": ["COP-GOV", "Certified Outsourcing Professional"],
  "cop-fp": ["COP-FP", "Certified Outsourcing Specialist"],
};

function normalizeCertNameKey(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, " ");
}

/** URL-safe token: "+" → "-plus", punctuation → hyphen, collapse repeats. */
function slugifyCertName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/\+/g, "-plus")
    .replace(/['\u2019]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Pre-cleanup slug: lowercase, spaces → hyphens, punctuation kept.
 * Used only to 308 old `/certifications/...` paths onto the canonical slug.
 */
export function legacyCertSlug(name: string): string {
  const key = normalizeCertNameKey(name);
  if (LEGACY_CERT_SLUG_BY_NAME[key]) return LEGACY_CERT_SLUG_BY_NAME[key];
  return name.toLowerCase().replace(/\s+/g, "-");
}

export function certSlug(name: string): string {
  const key = normalizeCertNameKey(name);
  if (CERT_SLUG_BY_NAME[key]) return CERT_SLUG_BY_NAME[key];
  return slugifyCertName(name);
}

function registerLegacyRedirect(name: string) {
  const to = certSlug(name);
  for (const from of [legacyCertSlug(name), slugifyCertName(name)]) {
    if (from && from !== to) CERT_SLUG_REDIRECTS[from] = to;
  }
}

for (const name of [
  "CompTIA A+",
  "CompTIA Network+",
  "CompTIA Security+",
  "CompTIA CySA+",
  "Azure Fundamentals (AZ-900)",
  "Azure Administrator (AZ-104)",
  "Azure Solutions Architect (AZ-305)",
  "Power Platform Developer Associate (PL-400)",
  "Microsoft Certified: Power Platform Developer Associate",
  "Microsoft Power Platform Developer Associate",
  "Certified Information Systems Auditor (CISA)",
  "Certified Information Systems Auditor",
  "CISA",
  "Certified Ethical Hacker (CEH)",
  "Scrum Master (CSM)",
  "Professional Scrum Master (PSM I)",
  "Certified Outsourcing Professional - Outsourcing Governance (COP-GOV)",
  "Certified Outsourcing Specialist- Foundation Principles (COP-FP)",
  "Certified Associate in Healthcare Information and Management Systems (CAHIMS)",
  "Certified Associate in Healthcare Information and Management Systems",
  "CAHIMS",
  "Certified Professional in Healthcare Information and Management Systems (CPHIMS)",
  "Certified Professional in Healthcare Information and Management Systems",
  "CPHIMS",
  "Registered Health Information Administrator (RHIA)",
  "Registered Health Information Administrator",
  "RHIA",
  "Registered Health Information Technician (RHIT)",
  "Registered Health Information Technician",
  "RHIT",
  "Certified Professional Coder (CPC)",
  "Certified Professional Coder",
  "CPC",
]) {
  registerLegacyRedirect(name);
}

/** If `slug` is a known legacy/guessed path, return the canonical slug. */
export function redirectCertSlug(slug: string): string | undefined {
  const s = decodeURIComponent(slug).trim();
  if (!s) return undefined;
  if (CERT_SLUG_REDIRECTS[s]) return CERT_SLUG_REDIRECTS[s];
  const canonical = certSlug(s);
  if (canonical && canonical !== s) return canonical;
  return undefined;
}

/** Canonical slug plus any legacy slugs that should still match stored rows. */
export function relatedCertSlugs(slug: string): string[] {
  const s = decodeURIComponent(slug).trim();
  const canonical = CERT_SLUG_REDIRECTS[s] ?? certSlug(s) ?? s;
  const aliases = Object.entries(CERT_SLUG_REDIRECTS)
    .filter(([, to]) => to === canonical)
    .map(([from]) => from);
  return [...new Set([canonical, s, ...aliases])];
}

export function extraCertCommunityTerms(slug: string): string[] {
  const canonical = relatedCertSlugs(slug)[0];
  return EXTRA_COMMUNITY_TERMS[canonical] ?? [];
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value).trim();
  } catch {
    return value.trim();
  }
}

/**
 * Canonical URL slug for a name or a (possibly legacy) path segment.
 * Always `[a-z0-9-]+` — never emit `+`, parentheses, or percent-encoding.
 */
export function canonicalCertSlug(slugOrName: string): string {
  const raw = safeDecode(slugOrName);
  if (!raw) return raw;
  return redirectCertSlug(raw) ?? certSlug(raw);
}

/** Canonical certification URL. Query-style `/certifications?cert=` redirects here. */
export function certHubHref(name: string): string {
  return `/certifications/${canonicalCertSlug(name)}`;
}

/** Short labels people type in search that map to a catalog/curated name. */
const CERT_SEARCH_ALIASES: Record<string, string> = {
  aws: "AWS Solutions Architect",
  "security+": "CompTIA Security+",
  "sec+": "CompTIA Security+",
  "security plus": "CompTIA Security+",
  "security-plus": "CompTIA Security+",
  "comptia security+": "CompTIA Security+",
  "comptia security plus": "CompTIA Security+",
  "comptia-security-plus": "CompTIA Security+",
  "comptia-security+": "CompTIA Security+",
  nclex: "NCLEX-RN",
  usmle: "USMLE Step 1",
  cpa: "CPA",
  cfa: "CFA Level 1",
  itil: "ITIL Foundation",
  teas: "ATI TEAS",
  "teas 7": "ATI TEAS",
  "ati teas": "ATI TEAS",
  "cna exam": "CNA",
  "nurse aide": "CNA",
  nnaap: "CNA",
  "nha ccma": "CCMA",
  "clinical medical assistant": "CCMA",
  "aapc cpc": "CPC",
  "certified professional coder": "CPC",
  "certified professional coder (cpc)": "CPC",
  hesi: "HESI A2",
  "hesi a2": "HESI A2",
  mls: "ASCP MLS",
  "ascp boc": "ASCP MLS",
  "ascp mls": "ASCP MLS",
  radiography: "ARRT Radiography",
  arrt: "ARRT Radiography",
  emt: "NREMT",
  "nremt emt": "NREMT",
  ptcb: "PTCE",
  "pharmacy technician": "PTCE",
  "aama cma": "Certified Medical Assistant (AAMA)",
  "cma aama": "Certified Medical Assistant (AAMA)",
  "cma (aama)": "Certified Medical Assistant (AAMA)",
  "certified medical assistant": "Certified Medical Assistant (AAMA)",
  "medical assistant aama": "Certified Medical Assistant (AAMA)",
  "usmle step 2 ck": "USMLE Step 2",
  "step 2 ck": "USMLE Step 2",
  "pl-400": "Power Platform Developer Associate (PL-400)",
  pl400: "Power Platform Developer Associate (PL-400)",
  "ab-400": "Power Platform Developer Associate (PL-400)",
  ab400: "Power Platform Developer Associate (PL-400)",
  "power platform developer": "Power Platform Developer Associate (PL-400)",
  "power platform developer associate": "Power Platform Developer Associate (PL-400)",
  "microsoft power platform developer associate": "Power Platform Developer Associate (PL-400)",
  "microsoft certified: power platform developer associate":
    "Power Platform Developer Associate (PL-400)",
  cisa: "Certified Information Systems Auditor (CISA)",
  "isaca cisa": "Certified Information Systems Auditor (CISA)",
  "certified information systems auditor": "Certified Information Systems Auditor (CISA)",
  "fnp-c": "FNP",
  "fnp-bc": "FNP",
  aanp: "FNP",
  "fnp board": "FNP",
  cahims: "CAHIMS",
  "certified associate in healthcare information and management systems": "CAHIMS",
  cphims: "CPHIMS",
  "certified professional in healthcare information and management systems": "CPHIMS",
  rhia: "RHIA",
  "registered health information administrator": "RHIA",
  rhit: "RHIT",
  "registered health information technician": "RHIT",
};

export function resolveCertSearchName(query: string): string {
  const trimmed = query.trim();
  if (!trimmed) return trimmed;
  return CERT_SEARCH_ALIASES[trimmed.toLowerCase()] ?? trimmed;
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

/** Formats `YYYY-MM` last-verified stamps for display (e.g. "2026-08" → "August 2026"). */
export function formatLastVerified(ym: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(ym.trim());
  if (!match) return ym;
  const month = Number(match[2]);
  if (month < 1 || month > 12) return ym;
  return `${MONTHS[month - 1]} ${match[1]}`;
}

export const EXAM_DETAILS_DISCLAIMER =
  "Exam details can change. Always confirm current requirements, fees, and exam policies with the certification provider before registering.";
